import { Box, Typography } from "@mui/material";
import { memo } from "react";
import { QUIZ } from "@/theme/quizTokens";
import {
    useExperimentValue,
    QUIZ_PRESENTATION_EXPERIMENT_KEY,
} from "@/experiments/useExperimentValue";

type Props = {
    days: number;
    planLabel: string;
    selected: boolean;
    totalDiscount: number;
    totalFull: number;
    discountedPricePerDay: number;
    fullPricePerDay: number;
    onSelect: () => void;
    /** Temporarily not purchasable on new plans — muted, unselectable, badge shows lockedLabel. */
    locked?: boolean;
    lockedLabel?: string;
};

const BEST_VALUE_DAYS = 28;

/** PoP red used for struck-through prices in the Figma (1521:924). */
const STRIKE_RED = "#ff7873";

/** Selection blue for the ring + radio dot. */
const SELECT_BLUE = "#2fb3e3";

/** Free-meal counts, matching the "3 / 7 / 14 meals free" pattern in the spec.
 *  NOTE: frontend-only formula (floor(days / 2)) — flag to product/backend if
 *  this should instead be sourced from the real pack/meal allocation. */
const FREE_MEALS_BY_DAYS: Record<number, number> = {
    7: 3,
    14: 7,
    28: 14,
};

function getFreeMealsCount(days: number): number {
    return FREE_MEALS_BY_DAYS[days] ?? Math.floor(days / 2);
}

/** Rung label shown above each card — replaced by lockedLabel when locked. */
const RUNG_LABEL_BY_DAYS: Record<number, string> = {
    7: "Try it",
    14: "Most popular",
};

/**
 * A plan row inside the brown "Choose your plan" card (Figma 1521:924 mobile /
 * 1586:11883 desktop): vertically-centred radio + Kuillo plan name + RL Aqva
 * price with red struck-through original, and a cream per-day pill below.
 * Selection is marked with a thick #2FB3E3 ring + matching radio dot.
 */
function PlanCard({
    days,
    planLabel,
    selected,
    totalDiscount,
    totalFull,
    discountedPricePerDay,
    fullPricePerDay,
    onSelect,
    locked = false,
    lockedLabel,
}: Props) {
    // Experiment lever (presentation only, default = current copy).
    const bestValueLabel = useExperimentValue(
        QUIZ_PRESENTATION_EXPERIMENT_KEY,
        "plan_badge_text",
        "Best value"
    );

    const handleSelect = () => {
        if (locked) return;
        onSelect();
    };

    // The lock badge replaces "Best value" — a locked plan shouldn't advertise.
    const badge = locked ? lockedLabel : days === BEST_VALUE_DAYS ? bestValueLabel : RUNG_LABEL_BY_DAYS[days] ?? null;
    const freeMeals = getFreeMealsCount(days);

    return (
        <Box
            onClick={handleSelect}
            onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelect();
            }}
            aria-disabled={locked || undefined}
            sx={{
                position: "relative",
                width: "100%",
                borderRadius: "999px",
                p: { xs: "12px 18px 12px 15px", md: "24px 40px 24px 28px" },
                cursor: locked ? "default" : "pointer",
                touchAction: "manipulation",
                userSelect: "none",
                WebkitTapHighlightColor: "transparent",
                background: QUIZ.color.white,
                border: `3px solid ${selected ? SELECT_BLUE : "transparent"}`,
                boxShadow: selected ? `0 0 0 1px ${SELECT_BLUE}` : "0 1px 3px rgba(0,0,0,0.08)",
                transition: "border 0.2s ease, box-shadow 0.2s ease",
                mb: { xs: "14px", md: "23px" },
                mt: { xs: "8px", md: "14px" },
                display: "flex",
                alignItems: "center",
                gap: { xs: "12px", md: "16px" },
            }}
        >
            {badge && (
                <Box
                    sx={{
                        position: "absolute",
                        top: { xs: -9, md: -16 },
                        left: "50%",
                        transform: "translateX(-50%)",
                        bgcolor: locked
                         ? QUIZ.color.cream3
                         : days === BEST_VALUE_DAYS
                         ? QUIZ.color.brown
                         : days === 7
                         ? QUIZ.color.cream3
                         : QUIZ.color.blue,
                        color: days === BEST_VALUE_DAYS && !locked ? QUIZ.color.white : QUIZ.color.ink,
                        fontFamily: QUIZ.font.body,
                        fontWeight: 700,
                        fontSize: { xs: 10, md: 14 },
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                        borderRadius: "999px",
                        px: { xs: "10px", md: "16px" },
                        py: { xs: "3px", md: "6px" },
                        whiteSpace: "nowrap",
                        opacity: 1,
                    }}
                >
                    {badge}
                </Box>
            )}

            {/* radio — vertically centred against the whole row (Figma) */}
            <Box
                sx={{
                    width: { xs: 17, md: 29 },
                    height: { xs: 17, md: 30 },
                    borderRadius: "50%",
                    flexShrink: 0,
                    border: `1.5px solid ${selected ? SELECT_BLUE : "#c9c2ba"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: locked ? 0.45 : 1,
                }}
            >
                {selected && (
                    <Box
                        sx={{
                            width: { xs: 9, md: 16 },
                            height: { xs: 9, md: 16 },
                            borderRadius: "50%",
                            bgcolor: SELECT_BLUE,
                        }}
                    />
                )}
            </Box>

            {/* content column */}
            <Box sx={{ flex: 1, minWidth: 0, opacity: locked ? 0.55 : 1 }}>
                {/* Top row: name/free-meals on the left, per-day price (headline) on the right */}
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontFamily: QUIZ.font.label,
                                fontSize: { xs: 17, md: 30 },
                                lineHeight: 1.245,
                                color: QUIZ.color.ink,
                                whiteSpace: "nowrap",
                            }}
                        >
                            {planLabel}
                        </Typography>

                        {/* Free-meal messaging */}
                        <Typography
                            sx={{
                                fontFamily: QUIZ.font.display,
                                fontWeight: 700,
                                fontSize: { xs: 11, md: 18 },
                                lineHeight: 1.2,
                                color: QUIZ.color.sage,
                                whiteSpace: "nowrap",
                            }}
                        >
                            {freeMeals} meals free
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: QUIZ.font.body,
                                fontSize: { xs: 10.5, md: 17 },
                                lineHeight: 1.2,
                                color: QUIZ.color.statLabel,
                                whiteSpace: "nowrap",
                            }}
                        >
                            plus {freeMeals} free in box 2
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: { xs: "5px", md: "8px" }, flexShrink: 0 }}>
                        <Typography
                            sx={{
                                fontFamily: QUIZ.font.display,
                                fontWeight: 900,
                                fontSize: { xs: 21, md: 40 },
                                lineHeight: 1,
                                color: QUIZ.color.ink,
                                whiteSpace: "nowrap",
                            }}
                        >
                            £{discountedPricePerDay.toFixed(2)}/day
                        </Typography>

                        {/* Cream total-price pill, now secondary */}
                        <Box
                            sx={{
                                borderRadius: "999px",
                                background: QUIZ.color.cream3,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "3px",
                                px: { xs: "12px", md: "20px" },
                                py: { xs: "3px", md: "6px" },
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                                fontFamily: QUIZ.font.body,
                                fontSize: { xs: 10.5, md: 17 },
                                color: QUIZ.color.ink,
                            }}
                        >
                            <span style={{ fontWeight: 700 }}>£{totalDiscount.toFixed(2)}</span>
                            <span style={{ color: STRIKE_RED, textDecoration: "line-through" }}>
                                £{totalFull.toFixed(2)}
                            </span>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default memo(PlanCard);